import React, { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { 
  Target, 
  Calendar, 
  TrendingUp, 
  DollarSign, 
  MessageSquare,
  Mail,
  Phone,
  Users,
  CheckCircle2
} from 'lucide-react';
import { campaignGoalsSchema, CampaignGoalsFormData } from '../../../schemas/onboarding';
import { 
  PRIMARY_OBJECTIVES, 
  TIMELINES, 
  VOLUME_EXPECTATIONS, 
  BUDGET_RANGES, 
  OUTREACH_CHANNELS 
} from '../../../constants/onboarding';
import { MultiSelect } from '../MultiSelect';

interface GoalsStepProps {
  data: Partial<CampaignGoalsFormData>;
  onNext: (data: CampaignGoalsFormData) => void;
  onBack: () => void;
  className?: string;
  onStepComplete?: (isComplete: boolean) => void;
}

export const GoalsStep: React.FC<GoalsStepProps> = ({
  data,
  onNext,
  onBack,
  className = '',
  onStepComplete,
}) => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<CampaignGoalsFormData>({
    resolver: zodResolver(campaignGoalsSchema),
    defaultValues: data,
    mode: 'onChange',
  });

  const watchedObjective = watch('primaryObjective');
  const watchedTimeline = watch('timeline');
  const watchedVolume = watch('volumeExpectations');
  const watchedChannels = watch('outreachChannels');

  // Create stable comparison values
  const channelsLength = useMemo(() => {
    return watchedChannels?.length || 0;
  }, [watchedChannels]);

  // Track step completion
  useEffect(() => {
    const hasBasicRequirements = !!watchedObjective && !!watchedTimeline && !!watchedVolume && 
                                 channelsLength > 0;
    onStepComplete?.(hasBasicRequirements);
  }, [watchedObjective, watchedTimeline, watchedVolume, channelsLength, onStepComplete]);

  const onSubmit = (formData: CampaignGoalsFormData) => {
    onNext(formData);
  };

  const outreachChannelOptions = OUTREACH_CHANNELS.map(channel => ({
    value: channel,
    label: channel,
  }));

  // Get icon for objectives
  const getObjectiveIcon = (objective: string) => {
    switch (objective) {
      case 'leads': return Users;
      case 'meetings': return MessageSquare;
      case 'sales': return TrendingUp;
      case 'brand_awareness': return Target;
      default: return Target;
    }
  };

  return (
    <div className={`max-w-3xl mx-auto px-6 py-8 ${className}`}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex p-3 bg-warning-50 rounded-full mb-4">
            <Target className="w-6 h-6 text-warning-500" />
          </div>
          <h2 className="text-3xl font-bold text-neutral-900 mb-2">
            Set Your Campaign Goals
          </h2>
          <p className="text-neutral-600">
            Define your objectives, timeline, and preferences to optimize your outreach strategy
          </p>
        </div>

        <form 
          onSubmit={(e) => {
            e.preventDefault(); // Prevent default form submission
          }}
          className="space-y-8"
        >
          {/* Primary Objective */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <label className="block text-lg font-semibold text-neutral-900 mb-4">
              What's your primary objective?
            </label>
            <div className="grid md:grid-cols-2 gap-4">
              {PRIMARY_OBJECTIVES.map((objective) => {
                const IconComponent = getObjectiveIcon(objective.value);
                const isSelected = watchedObjective === objective.value;
                
                return (
                  <motion.label
                    key={objective.value}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`
                      relative p-6 border-2 rounded-xl cursor-pointer transition-all duration-200
                      ${isSelected 
                        ? 'border-primary-500 bg-primary-50 shadow-glow-primary' 
                        : 'border-neutral-200 hover:border-primary-300 bg-white hover:shadow-medium'
                      }
                    `}
                  >
                    <input
                      {...register('primaryObjective')}
                      type="radio"
                      value={objective.value}
                      className="sr-only"
                    />
                    
                    <div className="flex items-start gap-4">
                      <div className={`
                        p-3 rounded-xl 
                        ${isSelected ? 'bg-primary-100' : 'bg-neutral-100'}
                      `}>
                        <IconComponent className={`
                          w-6 h-6 
                          ${isSelected ? 'text-primary-600' : 'text-neutral-600'}
                        `} />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className={`
                          text-lg font-semibold mb-2
                          ${isSelected ? 'text-primary-900' : 'text-neutral-900'}
                        `}>
                          {objective.label}
                        </h3>
                        <p className="text-neutral-600 text-sm">
                          {objective.description}
                        </p>
                      </div>
                      
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute top-4 right-4"
                        >
                          <CheckCircle2 className="w-6 h-6 text-primary-500" />
                        </motion.div>
                      )}
                    </div>
                  </motion.label>
                );
              })}
            </div>
            {errors.primaryObjective && (
              <p className="text-sm text-error-600 mt-2">{errors.primaryObjective.message}</p>
            )}
          </motion.div>

          {/* Timeline and Volume */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="grid md:grid-cols-2 gap-6"
          >
            {/* Timeline */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-3">
                <Calendar className="w-4 h-4 inline mr-2" />
                Campaign Timeline *
              </label>
              <div className="space-y-3">
                {TIMELINES.map((timeline) => (
                  <label
                    key={timeline}
                    className={`
                      flex items-center p-4 border rounded-xl cursor-pointer transition-all duration-200
                      ${watchedTimeline === timeline 
                        ? 'border-primary-500 bg-primary-50' 
                        : 'border-neutral-200 hover:border-neutral-300 bg-white'
                      }
                    `}
                  >
                    <input
                      {...register('timeline')}
                      type="radio"
                      value={timeline}
                      className="w-4 h-4 text-primary-600 border-neutral-300 focus:ring-primary-500"
                    />
                    <span className="ml-3 text-neutral-900 font-medium">{timeline}</span>
                  </label>
                ))}
              </div>
              {errors.timeline && (
                <p className="text-sm text-error-600 mt-2">{errors.timeline.message}</p>
              )}
            </div>

            {/* Volume Expectations */}
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-3">
                <TrendingUp className="w-4 h-4 inline mr-2" />
                Volume Expectations *
              </label>
              <div className="space-y-3">
                {VOLUME_EXPECTATIONS.map((volume) => (
                  <label
                    key={volume}
                    className={`
                      flex items-center p-4 border rounded-xl cursor-pointer transition-all duration-200
                      ${watchedVolume === volume 
                        ? 'border-primary-500 bg-primary-50' 
                        : 'border-neutral-200 hover:border-neutral-300 bg-white'
                      }
                    `}
                  >
                    <input
                      {...register('volumeExpectations')}
                      type="radio"
                      value={volume}
                      className="w-4 h-4 text-primary-600 border-neutral-300 focus:ring-primary-500"
                    />
                    <span className="ml-3 text-neutral-900 font-medium">{volume}</span>
                  </label>
                ))}
              </div>
              {errors.volumeExpectations && (
                <p className="text-sm text-error-600 mt-2">{errors.volumeExpectations.message}</p>
              )}
            </div>
          </motion.div>

          {/* Budget */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            <label className="block text-sm font-semibold text-neutral-700 mb-3">
              <DollarSign className="w-4 h-4 inline mr-2" />
              Budget Range *
            </label>
            <div className="grid md:grid-cols-3 gap-3">
              {BUDGET_RANGES.map((budget) => (
                <label
                  key={budget}
                  className={`
                    flex items-center justify-center p-4 border rounded-xl cursor-pointer transition-all duration-200
                    ${watch('budget') === budget 
                      ? 'border-primary-500 bg-primary-50 text-primary-700' 
                      : 'border-neutral-200 hover:border-neutral-300 bg-white text-neutral-900'
                    }
                  `}
                >
                  <input
                    {...register('budget')}
                    type="radio"
                    value={budget}
                    className="sr-only"
                  />
                  <span className="font-medium text-center">{budget}</span>
                </label>
              ))}
            </div>
            {errors.budget && (
              <p className="text-sm text-error-600 mt-2">{errors.budget.message}</p>
            )}
          </motion.div>

          {/* Outreach Channels */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
          >
            <label className="block text-sm font-semibold text-neutral-700 mb-3">
              Preferred Outreach Channels *
            </label>
            <Controller
              name="outreachChannels"
              control={control}
              render={({ field }) => (
                <MultiSelect
                  options={outreachChannelOptions}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Select your preferred channels"
                  suggestions={['Email', 'LinkedIn', 'Cold Calling']}
                  error={errors.outreachChannels?.message}
                />
              )}
            />
            <p className="text-sm text-neutral-500 mt-2">
              Choose the channels that work best for your target audience and team
            </p>
          </motion.div>

          {/* Additional Notes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.5 }}
          >
            <label className="block text-sm font-semibold text-neutral-700 mb-2">
              Additional Notes (Optional)
            </label>
            <textarea
              {...register('additionalNotes')}
              rows={4}
              placeholder="Any specific requirements, constraints, or preferences for your campaign..."
              className="w-full px-4 py-3 border border-neutral-300 rounded-xl transition-all duration-200 ease-out resize-none
                        bg-neutral-50 focus:bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10"
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-sm text-neutral-500">
                Help us understand any special considerations
              </p>
              <span className="text-sm text-neutral-400">
                {watch('additionalNotes')?.length || 0}/500
              </span>
            </div>
          </motion.div>

          {/* Expected Results */}
          {watchedObjective && watchedVolume && watchedTimeline && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.6 }}
              className="bg-gradient-to-br from-success-50 to-primary-50 rounded-2xl p-6"
            >
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                Expected Results Preview
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-600 mb-1">
                    {watchedVolume.split(' ')[0]}
                  </div>
                  <div className="text-sm text-neutral-600">Contacts Reached</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary-600 mb-1">
                    {Math.round(parseInt(watchedVolume.split('-')[0]) * 0.15)}-{Math.round(parseInt(watchedVolume.split('-')[0]) * 0.25)}
                  </div>
                  <div className="text-sm text-neutral-600">Expected Responses</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-warning-600 mb-1">
                    {watchedTimeline.split(' ')[0]}
                  </div>
                  <div className="text-sm text-neutral-600">Timeline</div>
                </div>
              </div>
              <p className="text-sm text-neutral-600 mt-4 text-center">
                These are estimates based on industry benchmarks. Actual results may vary.
              </p>
            </motion.div>
          )}

          {/* Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.7 }}
            className="flex items-center justify-between pt-6"
          >
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-3 text-neutral-600 hover:text-neutral-900 font-medium transition-colors"
            >
              Back
            </button>
            
            <button
              type="button"
              onClick={() => {
                handleSubmit(onSubmit)();
              }}
              disabled={!isValid}
              className={`
                px-8 py-3 rounded-xl font-semibold transition-all duration-200
                ${isValid
                  ? 'bg-primary-500 hover:bg-primary-600 text-white shadow-glow-primary hover:shadow-strong'
                  : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                }
              `}
            >
              Review & Launch
            </button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
}; 